import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Button, Col, Modal, Row, Select, Steps } from "antd"
import * as com from "../Common"
import CreditSummaryTableV2 from "./CreditSummaryTableV2"
import {
    PROPERTY_ACCOUNT_TYPES,
    buildCreditImportPayload,
    buildMortgageLinkRows,
    formatCreditOwnershipLabel,
    listLinkableProperties,
    resolveCreditImportTargetPartyId,
    resolveCreditReportNavigationPartyId,
    resolveOwnershipReportContext,
} from "../CreditReportV2/creditImportV2"
import { postLosCreditImportV2 } from "../DocumentChecklist/applicationV2Client"
import { track } from 'statsig'
import { STATSIG } from 'statsig/events'
import "./CreditSummaryReview.css"

const LinkIcon = ({ linked }) => (
    linked ? (
        <svg xmlns="http://www.w3.org/2000/svg" width="19" height="18" viewBox="0 0 19 18" fill="none">
            <path d="M6.13037 8.25H12.1304V9.75H6.13037V8.25ZM15.2054 9H16.6304C16.6304 6.93 14.9504 5.25 12.8804 5.25H9.88037V6.675H12.8804C14.1629 6.675 15.2054 7.7175 15.2054 9ZM3.05537 9C3.05537 7.7175 4.09787 6.675 5.38037 6.675H8.38037V5.25H5.38037C3.31037 5.25 1.63037 6.93 1.63037 9C1.63037 11.07 3.31037 12.75 5.38037 12.75H8.38037V11.325H5.38037C4.09787 11.325 3.05537 10.2825 3.05537 9Z" fill="#325CEB"/>
            <path d="M11.1304 12.5L12.6304 14L15.6304 11" stroke="#325CEB" strokeWidth="1.5" strokeLinecap="square"/>
        </svg>
    ) : (
        <svg xmlns="http://www.w3.org/2000/svg" width="19" height="18" viewBox="0 0 19 18" fill="none">
            <path d="M12.8805 5.25H9.88049V6.675H12.8805C14.163 6.675 15.2055 7.7175 15.2055 9C15.2055 10.0725 14.4705 10.9725 13.473 11.235L14.568 12.33C15.7905 11.7075 16.6305 10.4625 16.6305 9C16.6305 6.93 14.9505 5.25 12.8805 5.25ZM12.1305 8.25H10.488L11.988 9.75H12.1305V8.25ZM1.63049 3.2025L3.96299 5.535C3.27389 5.81538 2.68396 6.29475 2.26851 6.91191C1.85306 7.52907 1.63094 8.25603 1.63049 9C1.63049 11.07 3.31037 12.75 5.38037 12.75H8.38037V11.325H5.38037C4.09787 11.325 3.05537 10.2825 3.05537 9C3.05587 7.8075 3.96299 6.825 5.12549 6.6975L6.67799 8.25H6.13049V9.75H8.17799L9.88049 11.4525V12.75H11.178L14.1855 15.75L15.1305 14.805L2.58299 2.25L1.63049 3.2025Z" fill="#CB8600"/>
        </svg>
    )
)

const rowKey = (row, index = 0) => `${row.key || row.accountIdentifier || row.id || row.creditorName || "row"}:${index}`

const resolveLinkablePropertyDetails = (application, property) => {
    if (!application || !property) return {}
    if (property.target === "subject") {
        const subject = application.property || {}
        return {
            address: property.address || subject.address || "",
            propertyValue: subject.appraisal || subject.salesprice || "",
            propertytype: subject.propertytype || subject.propertyType || "",
            occupancy: subject.occupancy || "",
            status: subject.status || "",
            insurancemaintenancetaxes: subject.insurancemaintenancetaxes,
        }
    }
    const reo = application?.finance?.realEstateByPartyId?.[property.partyId]?.properties?.[property.propertyIndex] || {}
    return {
        address: property.address || reo.address || reo.street || "",
        propertyValue: reo.propertyValue || reo.appraisal || reo.marketvalue || "",
        propertytype: reo.propertytype || reo.propertyType || "",
        occupancy: reo.occupancy || "",
        status: reo.status || "",
        insurancemaintenancetaxes: reo.insurancemaintenancetaxes,
    }
}

const isPropertyIncomplete = (details) => {
    const requiredFields = ["address", "insurancemaintenancetaxes", "status", "propertytype", "occupancy"]
    return requiredFields.some((field) => {
        const value = details[field]
        return value === "" || value === null || value === undefined
    })
}

const OCCUPANCY_LABELS = {
    principal: "Primary Residence",
    secondhome: "Second Home",
    investment: "Investment",
    fhasecond: "FHA Secondary Residence",
    other: "Other",
}

const STATUS_LABELS = {
    sold: "Sold",
    pending: "Pending sale",
    retained: "Retained",
}

const SingleFamilyIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="14" viewBox="0 0 15 14" fill="none">
        <path d="M2.85471 7.00023H1.53847L7.46154 1.07715L13.3846 7.00023H12.0684" stroke="#586570" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M2.85471 7.00023V11.6071C2.85471 11.9562 2.99338 12.2909 3.24022 12.5378C3.48707 12.7846 3.82186 12.9233 4.17095 12.9233H10.7521C11.1012 12.9233 11.436 12.7846 11.6829 12.5378C11.9297 12.2909 12.0684 11.9562 12.0684 11.6071V7.00023" stroke="#586570" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6.14531 7.00023H8.77778V9.6327H6.14531V7.00023Z" stroke="#586570" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const TownhouseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M5.70367 12.3847V1.61548L12.8333 7.96656" stroke="#586570" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12.1852 7.59848C12.1852 8.69535 12.1852 10.231 12.1852 11.1882C12.1852 12.1455 11.321 12.3848 10.8889 12.3848C8.51235 12.3848 3.50001 12.3848 2.46297 12.3848C1.42593 12.3848 1.16667 11.5871 1.16667 11.1882V5.20532H5.37963" stroke="#586570" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const MultiFamilyIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="12" viewBox="0 0 14 12" fill="none">
        <path d="M10.7692 1.15381L12.3846 5.4615" stroke="#586570" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3.23077 1.15381L1.61539 5.4615" stroke="#586570" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M7 5.46143L7 10.846" stroke="#586570" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3.23077 8.15381H10.7692" stroke="#586570" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M2.69231 5.86182V9.89259C2.69231 10.219 2.82198 10.5321 3.05279 10.7629C3.2836 10.9937 3.59666 11.1234 3.92308 11.1234H10.0769C10.4033 11.1234 10.7164 10.9937 10.9472 10.7629C11.178 10.5321 11.3077 10.219 11.3077 9.89259V5.86182" stroke="#586570" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M1.61539 5.46143L12.3846 5.46142" stroke="#586570" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3.23077 1.15381H10.7692" stroke="#586570" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const CondoIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M3.56114 7.67627H5.58037V11.0417H3.56114V7.67627Z" stroke="#586570" strokeWidth="1.07692" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M1.15819 1.1582H10.9355" stroke="#586570" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M1.15819 6.09399H10.9355" stroke="#586570" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M1.70137 1.70142V9.85863C1.70137 10.4534 2.18353 10.9356 2.77829 10.9356H9.31541C9.91017 10.9356 10.3923 10.4534 10.3923 9.85864V1.70142" stroke="#586570" strokeWidth="1.07692" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const getPropertyTypeTag = (propertyType) => {
    const normalized = `${propertyType || ""}`.trim().toLowerCase()
    const propertyTypeOptions = {
        singlefamily: { icon: <SingleFamilyIcon />, label: "Single family" },
        singlefamilyattached: { icon: <SingleFamilyIcon />, label: "Single family" },
        townhouse: { icon: <TownhouseIcon />, label: "Townhouse" },
        singlefamilydetached: { icon: <TownhouseIcon />, label: "Townhouse" },
        twotofour: { icon: <MultiFamilyIcon />, label: "Multi-family" },
        twotofourfamily: { icon: <MultiFamilyIcon />, label: "Multi-family" },
        condominium: { icon: <CondoIcon />, label: "Condo" },
    }
    const option = propertyTypeOptions[normalized]
    if (!option) return null
    return (
        <div className="property-type-tag">
            {option.icon}
            <span className="property-type-label">{option.label}</span>
        </div>
    )
}

const PropertyLinkOption = ({ property, details }) => (
    <div className="property-option" style={{ display: "flex", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
            <div className="property-top">
                <div className="property-title">{details.address || property.shortAddress}</div>
                {details.propertyValue ? (
                    <div className="property-price">${com.commaize(String(details.propertyValue))}</div>
                ) : null}
            </div>
            <div className="property-middle">
                <div>{property.participantName} - {property.sectionDescription}</div>
                {isPropertyIncomplete(details) ? (
                    <div className="property-incomplete">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <rect width="18" height="18" rx="9" fill="#CB8600" />
                            <rect x="8" y="4" width="2" height="7" rx="1" fill="white" />
                            <rect x="10" y="12" width="2" height="2" rx="1" transform="rotate(90 10 12)" fill="white" />
                        </svg>
                        <div>Incomplete</div>
                    </div>
                ) : null}
            </div>
            <div className="property-bottom">
                <div className="property-tag-list">
                    {getPropertyTypeTag(details.propertytype)}
                    <div className="property-occupancy-tag">
                        {OCCUPANCY_LABELS[details.occupancy] || details.occupancy || ""}
                    </div>
                </div>
                <div className="property-status">
                    {STATUS_LABELS[details.status] || details.status || ""}
                </div>
            </div>
        </div>
    </div>
)

export default function CreditSummaryReviewV2({
    open,
    loanId,
    report,
    application,
    accounts = [],
    initialExcludeZeroBalance = true,
    initialExcludeClosed = true,
    loading = false,
    defaultCheckedType = "all",
    ownershipReportContext: ownershipReportContextProp,
    onCancel,
    onComplete,
}) {
    const [selectedRows, setSelectedRows] = useState([])
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [importComplete, setImportComplete] = useState(false)
    const [importing, setImporting] = useState(false)
    const [step, setStep] = useState(0)
    const [status, setStatus] = useState(["process", "wait"])
    const [section3Info, setSection3Info] = useState({ count: 0, handleSection3Select: () => {} })
    const [selectedLinks, setSelectedLinks] = useState({})
    const [importTargetPartyId, setImportTargetPartyId] = useState("")

    const section3Rows = useMemo(
        () => selectedRows.filter((row) => PROPERTY_ACCOUNT_TYPES.includes(row.accountType)),
        [selectedRows],
    )
    const hasSection3Selected = section3Rows.length > 0
    const hasSection2cSelected = selectedRows.some((row) => !PROPERTY_ACCOUNT_TYPES.includes(row.accountType))
    const ownershipReportContext = useMemo(() => {
        if (ownershipReportContextProp) return ownershipReportContextProp
        if (report && application) return resolveOwnershipReportContext(report, application)
        return null
    }, [ownershipReportContextProp, report, application])
    const linkableProperties = useMemo(
        () => listLinkableProperties(application, ownershipReportContext?.subjectPartyIds),
        [application, ownershipReportContext],
    )
    const formatOwnershipLabel = useCallback((text, record) => (
        formatCreditOwnershipLabel(
            { ...record, ownership: text || record?.ownership },
            ownershipReportContext,
            application,
        )
    ), [application, ownershipReportContext])
    const ownershipLabelForRow = useCallback((row) => (
        formatCreditOwnershipLabel(row, ownershipReportContext, application)
    ), [application, ownershipReportContext])
    const defaultNavigationPartyId = useMemo(
        () => resolveCreditReportNavigationPartyId(report, application),
        [report, application],
    )
    const finishNavigation = useCallback((link) => {
        if (!onComplete) return
        onComplete({
            link,
            partyId: importTargetPartyId || defaultNavigationPartyId,
        })
    }, [onComplete, importTargetPartyId, defaultNavigationPartyId])

    useEffect(() => {
        if (!open) {
            setStep(0)
            setStatus(["process", "wait"])
            setSelectedLinks({})
            setSelectedRows([])
            setImportTargetPartyId("")
        }
    }, [open])

    useEffect(() => {
        setSelectedLinks((prev) => {
            const next = { ...prev }
            section3Rows.forEach((row, index) => {
                const key = rowKey(row, index)
                if (!next[key]) next[key] = []
            })
            Object.keys(next).forEach((key) => {
                if (!section3Rows.some((row, index) => rowKey(row, index) === key)) {
                    delete next[key]
                }
            })
            return next
        })
    }, [section3Rows])

    useEffect(() => {
        const allowed = new Set(linkableProperties.map((p) => p.linkId))
        setSelectedLinks((prev) => {
            let changed = false
            const next = {}
            Object.entries(prev).forEach(([key, linkIds]) => {
                const filtered = (linkIds || []).filter((id) => allowed.has(id))
                if (filtered.length !== (linkIds || []).length) changed = true
                next[key] = filtered
            })
            return changed ? next : prev
        })
    }, [linkableProperties])

    const isAnyPropertyUnselected = step === 1 && section3Rows.some((row, index) => {
        const key = rowKey(row, index)
        return !(selectedLinks[key] || []).length
    })

    const isImportDisabled = selectedRows.length === 0 || isAnyPropertyUnselected

    const buildPropertyOptionsForRow = useCallback((linkKey) => {
        const selectedLinkIds = selectedLinks[linkKey] || []
        return linkableProperties.map((property) => {
            const details = resolveLinkablePropertyDetails(application, property)
            const partyAlreadySelected = selectedLinkIds.some((linkId) => {
                const selected = linkableProperties.find((item) => item.linkId === linkId)
                return selected?.partyId === property.partyId && linkId !== property.linkId
            })
            return {
                value: property.linkId,
                displayLabel: property.shortAddress,
                disabled: partyAlreadySelected,
                label: <PropertyLinkOption property={property} details={details} />,
            }
        })
    }, [linkableProperties, application, selectedLinks])

    const handleSelectChange = (row, index, options) => {
        const key = rowKey(row, index)
        const byParty = new Map()
        ;(options || []).forEach((opt) => {
            const property = linkableProperties.find((item) => item.linkId === opt.value)
            if (property?.partyId) {
                byParty.set(property.partyId, opt.value)
            }
        })
        setSelectedLinks((prev) => ({
            ...prev,
            [key]: [...byParty.values()],
        }))
    }

    const handleClear = (row, index) => {
        const key = rowKey(row, index)
        setSelectedLinks((prev) => ({ ...prev, [key]: [] }))
    }

    const stepChange = (value) => {
        setStatus((prev) => prev.map((item, index) => {
            if (index === value) return "process"
            if (index < value) return "finish"
            return "wait"
        }))
        setStep(value)
    }

    const handleConfirm = () => {
        if (hasSection3Selected && step === 0) {
            stepChange(1)
            return
        }
        setConfirmOpen(true)
    }

    const handleConfirmImport = async () => {
        if (!report || !application || selectedRows.length === 0) return
        setImporting(true)
        try {
            const mortgageLinks = buildMortgageLinkRows(section3Rows, selectedLinks)
            const payload = buildCreditImportPayload(report, application, selectedRows, mortgageLinks)
            await postLosCreditImportV2(loanId, payload)
            const section = hasSection2cSelected && hasSection3Selected
                ? 'section2c_and_section3'
                : (hasSection3Selected ? 'section3' : 'section2c')
            track(STATSIG.LOAN.IMPORT_FROM_CREDIT_REPORT, 'insert', {
                type: 'insert',
                section,
            })
            setImportTargetPartyId(
                resolveCreditImportTargetPartyId(report, application, selectedRows, selectedLinks),
            )
            setConfirmOpen(false)
            setImportComplete(true)
        } catch (err) {
            alert(err.message || "Failed to import liabilities.")
        } finally {
            setImporting(false)
        }
    }

    let confirmSectionText = ""
    if (hasSection2cSelected && hasSection3Selected) confirmSectionText = "Section 2c and Section 3"
    else if (hasSection2cSelected) confirmSectionText = "Section 2c"
    else if (hasSection3Selected) confirmSectionText = "Section 3"

    const completeLink = hasSection3Selected && !hasSection2cSelected ? "#3a" : "#2c"

    const footer = [
        <Button
            key="back"
            type="link"
            className="credit-summary-review-modal-footer-back"
            onClick={() => {
                if (step === 0) onCancel()
                else stepChange(0)
            }}
        >
            {step === 0 ? "Back to credit summary" : "Back to liability selection"}
        </Button>,
        <div key="confirm" className="credit-summary-review-modal-footer-confirm">
            <Button
                type="primary"
                disabled={isImportDisabled}
                className="credit-summary-review-modal-footer-confirm-button"
                onClick={handleConfirm}
            >
                {hasSection3Selected && step === 0 ? "Next" : "Import to 1003"}
            </Button>
        </div>,
    ]

    const confirmFooter = [
        <Button key="cancel" type="link" className="credit-summary-confirm-modal-back" onClick={() => setConfirmOpen(false)}>
            Cancel
        </Button>,
        <Button
            key="import"
            type="primary"
            className="credit-summary-confirm-modal-import"
            loading={importing}
            onClick={handleConfirmImport}
        >
            Yes, import to 1003
        </Button>,
    ]

    const tagRender = (props) => {
        const { label, closable, onClose } = props
        return (
            <span style={{
                marginRight: 4,
                display: "inline-flex",
                alignItems: "center",
                gap: 20,
                backgroundColor: "#EAEFFD",
                borderRadius: 8,
                height: 34,
                padding: "0px 16px 0px 12px",
                fontSize: 16,
                fontFamily: "Inter",
                color: "#325CEB",
                fontWeight: 500,
            }}
            >
                {label}
                {closable && (
                    <span style={{ cursor: "pointer" }} onClick={onClose}>
                        ×
                    </span>
                )}
            </span>
        )
    }

    return (
        <>
            <Modal
                className="credit-summary-review-modal"
                maskStyle={{ backgroundColor: "rgba(0, 0, 0, 0.75)" }}
                maskClosable={false}
                width={974}
                open={open}
                centered
                destroyOnClose
                title="Review"
                onCancel={onCancel}
                footer={footer}
            >
                <div className="credit-summary-review-modal-content">
                    {hasSection3Selected && (
                        <div className="credit-summary-steps-wrapper">
                            <Steps
                                className="credit-summary-steps"
                                size="small"
                                current={step}
                                onChange={stepChange}
                                items={[
                                    { title: "Select liabilities" },
                                    { title: "Match mortgage to properties" },
                                ]}
                            />
                        </div>
                    )}
                    {step === 0 && (
                        <div className="credit-summary-review-modal-content-title">
                            Review the updated liabilities from your credit report or check the original data in 1003
                            {" "}
                            <span className="link-text" onClick={() => finishNavigation("#2c")}>Section 2c.</span>
                            {hasSection3Selected && (
                                <>
                                    {" "}
                                    &amp;
                                    {" "}
                                    <span className="link-text" onClick={() => finishNavigation("#3a")}>Section 3.</span>
                                </>
                            )}
                        </div>
                    )}
                    {step === 0 && defaultCheckedType === "section2c" && section3Info.count > 0 && (
                        <div style={{ marginRight: 0 }} className="credit-summary-review-warning-card">
                            <div className="credit-summary-review-warning-content">
                                <div className="credit-summary-review-warning-content-title">Additional mortgage loan found</div>
                                <div className="credit-summary-review-warning-content-text">
                                    {section3Info.count} mortgage loan{section3Info.count > 1 ? "s" : ""} also available under Section 3.
                                </div>
                                <div
                                    className="credit-summary-review-warning-content-button"
                                    onClick={() => {
                                        if (section3Info.handleSection3Select) section3Info.handleSection3Select(true)
                                    }}
                                >
                                    Select to import
                                </div>
                            </div>
                        </div>
                    )}
                    <div style={step === 0 ? { paddingRight: 50 } : {}}>
                        <div style={{ display: step === 0 ? "block" : "none" }}>
                            <CreditSummaryTableV2
                                type="grouped"
                                loading={loading}
                                accounts={accounts}
                                useCheckbox
                                initialExcludeZeroBalance={initialExcludeZeroBalance}
                                initialExcludeClosed={initialExcludeClosed}
                                onSelectedRowsChange={setSelectedRows}
                                defaultCheckedType={defaultCheckedType}
                                onSection3InfoChange={setSection3Info}
                                formatOwnershipLabel={ownershipReportContext && !ownershipReportContext.error
                                    ? formatOwnershipLabel
                                    : undefined}
                                ownershipEditable={false}
                            />
                        </div>
                        <div style={{ display: step === 1 ? "flex" : "none" }} className="link-mortgage-content">
                            <div className="link-mortgage-content-title">Link mortgage liabilities to properties listed in Section 3.</div>
                            {linkableProperties.length === 0 && (
                                <div style={{ marginRight: "46px" }} className="credit-summary-review-warning-card">
                                    <div className="credit-summary-review-warning-content">
                                        <div className="credit-summary-review-warning-content-title">No properties</div>
                                        <div className="credit-summary-review-warning-content-text">
                                            No properties are currently listed in Section 3. Please add a property before linking this liability.
                                        </div>
                                        <div className="credit-summary-review-warning-content-button" onClick={() => finishNavigation("#3a")}>
                                            Add property in Section 3
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="link-mortgage-list">
                                {section3Rows.map((row, rowIndex) => {
                                    const key = rowKey(row, rowIndex)
                                    const linked = (selectedLinks[key] || []).length > 0
                                    const remainingBalance = Math.floor(Number(row.unpaidBalanceAmount || 0))
                                    const monthlyPayment = Math.floor(Number(row.monthlyPaymentAmount || 0))
                                    const ownership = ownershipLabelForRow(row)
                                    return (
                                        <div key={key} className="link-mortgage-list-item">
                                            <div className="link-mortgage-list-item-header">
                                                <div className="link-mortgage-item-title">
                                                    <div className="link-mortgage-item-title-text">
                                                        <span>{row.creditorName}</span>
                                                    </div>
                                                    <div className={`link-mortgage-item-title-link property-link ${linked ? "linked" : "unlinked"}`}>
                                                        <LinkIcon linked={linked} />
                                                        {linked ? "Linked" : "Unlinked"}
                                                    </div>
                                                </div>
                                                <div className="link-mortgage-item-account-number">
                                                    Account number:
                                                    {" "}
                                                    {row.accountIdentifier || "--"}
                                                </div>
                                            </div>
                                            <div className="link-mortgage-item-content">
                                                <Row gutter={[64, 24]}>
                                                    <Col md={6} span={24}>
                                                        <div className="link-mortgage-item-title">Account type</div>
                                                        <div className="link-mortgage-item-value">{row.accountType}</div>
                                                    </Col>
                                                    <Col md={6} span={24}>
                                                        <div className="link-mortgage-item-title">Company name</div>
                                                        <div className="link-mortgage-item-value">{row.creditorName}</div>
                                                    </Col>
                                                    <Col md={6} span={24}>
                                                        <div className="link-mortgage-item-title">Unpaid balance</div>
                                                        <div className="link-mortgage-item-value">
                                                            $
                                                            {remainingBalance ? com.commaize(String(remainingBalance)) : "-"}
                                                        </div>
                                                    </Col>
                                                    <Col md={6} span={24}>
                                                        <div className="link-mortgage-item-title">Mo. payment</div>
                                                        <div className="link-mortgage-item-value">
                                                            $
                                                            {monthlyPayment ? com.commaize(String(monthlyPayment)) : "-"}
                                                        </div>
                                                    </Col>
                                                    <Col md={6} span={24}>
                                                        <div className="link-mortgage-item-title">Ownership</div>
                                                        <div className="link-mortgage-item-value">
                                                            {ownership}
                                                        </div>
                                                    </Col>
                                                </Row>
                                                <Row style={{ marginTop: 32 }}>
                                                    <Col md={24} span={24}>
                                                        <div className="link-mortgage-item-title">Select a property</div>
                                                        <div style={{ position: "relative", width: "100%" }}>
                                                        <Select
                                                            mode="multiple"
                                                            className="link-mortgage-item-select"
                                                            style={{ width: "100%" }}
                                                            listHeight={272}
                                                            getPopupContainer={(triggerNode) => triggerNode.parentElement}
                                                            placeholder={linkableProperties.length === 0 ? "No properties are currently listed" : "Select a property from Section 3"}
                                                            options={buildPropertyOptionsForRow(key)}
                                                            value={(selectedLinks[key] || []).map((linkId) => {
                                                                const property = linkableProperties.find((item) => item.linkId === linkId)
                                                                return {
                                                                    value: linkId,
                                                                    label: property?.shortAddress || linkId,
                                                                }
                                                            })}
                                                            labelInValue
                                                            onChange={(options) => handleSelectChange(row, rowIndex, options)}
                                                            optionLabelProp="displayLabel"
                                                            tagRender={tagRender}
                                                        />
                                                        {(selectedLinks[key] || []).length > 0 && (
                                                            <Button type="link" onClick={() => handleClear(row, rowIndex)} style={{ paddingLeft: 0 }}>
                                                                Clear
                                                            </Button>
                                                        )}
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>

            <Modal
                className={importComplete ? "credit-summary-review-complete-modal" : "credit-summary-review-confirm-modal"}
                maskStyle={{ backgroundColor: "rgba(0, 0, 0, 0.75)" }}
                maskClosable={false}
                width={importComplete ? 568 : 410}
                open={confirmOpen || importComplete}
                centered
                title={importComplete ? null : "Confirm"}
                onCancel={() => { setConfirmOpen(false); setImportComplete(false) }}
                footer={importComplete ? null : confirmFooter}
            >
                {importComplete ? (
                    <div className="credit-summary-complete-content">
                        <img src="/images/credit-import-complete.svg" alt="" />
                        <div className="credit-summary-complete-content-title">Liabilities Successfully Imported</div>
                        <div className="credit-summary-complete-content-text">
                            {`Liabilities have been successfully imported into 1003 ${confirmSectionText}. You can now review or update the imported data.`}
                        </div>
                        <div className="credit-summary-complete-content-button-group">
                            <Button
                                className="credit-summary-complete-back-button"
                                onClick={() => { setConfirmOpen(false); setImportComplete(false); onCancel() }}
                            >
                                Stay in credit report
                            </Button>
                            <Button
                                type="primary"
                                className="credit-summary-complete-continue-button"
                                onClick={() => {
                                    setImportComplete(false)
                                    finishNavigation(completeLink)
                                }}
                            >
                                Go to 1003
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="credit-summary-confirm-text">
                        <div>
                            {`You're about to import liabilities from the selected credit report into 1003 ${confirmSectionText}.`}
                            {" "}
                            Previously imported credit-report liabilities for the affected participants will be replaced.
                            Manually entered liabilities will be kept.
                        </div>
                        <div>Would you like to proceed?</div>
                    </div>
                )}
            </Modal>
        </>
    )
}
